import {
  EquipmentSlot,
  InventoryItem,
} from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Slider } from "@radix-ui/themes";
import { cn } from "@/core/helpers/cn";
import { SoundButton, TextInput } from "@/shared/ui";
import { ItemType } from "@/domains/inventory/constants/itemType";
import { determineEquipmentSlotType } from "@/domains/inventory/helpers/determineEquipmentSlotType";
import InventoryContextMenuButton from "@/domains/inventory/components/contextMenu/InventoryContextMenuButton";

type Props = {
  equipmentId?: number;
  className?: string;
  position: { x: number; y: number };
  item: InventoryItem;
  onApply?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onDrop?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onEquip?: (equipmentId: number, slotItem: EquipmentSlot) => void | Promise<void>;
  onUnEquip?: (equipmentId: number, slotType: string, item: InventoryItem) => Promise<void> | void;
  onDetach?: (
    fromInventoryId: number,
    toInventoryId: number,
    fromItem: InventoryItem,
    toItem: InventoryItem,
  ) => void | Promise<void>;
  onTrade?: () => void | Promise<void>;
  onMove?: (
    fromInventoryId: number,
    toInventoryId: number,
    item: InventoryItem,
    oldItem?: InventoryItem,
  ) => Promise<void>;
  onSplit?: (
    fromInventoryId: number,
    inventoryItemId: string,
    quantity: number,
  ) => void | Promise<void>;
  onAddItemToConfiscation?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
};

const InventoryContextMenu = forwardRef<HTMLDivElement, Props>(
  (
    {
      equipmentId,
      className,
      position,
      item,
      onApply,
      onDetach,
      onDrop,
      onEquip,
      onUnEquip,
      onTrade,
      onMove,
      onSplit,
      onClose,
      onRefresh,
      onAddItemToConfiscation,
    },
    ref,
  ) => {
    const [splitPosition, setSplitPosition] = useState<"right" | "left">("right");
    const [openSplit, setOpenSplit] = useState(false);
    const [splitQuantity, setSplitQuintity] = useState("1");
    const action = useCallback(
      async (cb: () => void | Promise<void>) => {
        await onClose?.();
        await cb();
        await onRefresh?.();
      },
      [onClose, onRefresh],
    );

    const toggleSplit = useCallback(() => {
      setOpenSplit((state) => !state);
    }, []);

    const calculateSplitPosition = useCallback((menuElement: HTMLDivElement | null) => {
      if (!menuElement) return;

      const menuRect = menuElement.getBoundingClientRect();
      const splitWidth = 240;
      const rightSpace = window.innerWidth - menuRect.right - 12;

      if (rightSpace >= splitWidth) {
        setSplitPosition("right");
      } else {
        setSplitPosition("left");
      }
    }, []);

    useEffect(() => {
      if (openSplit) {
        calculateSplitPosition(ref.current);
      }
    }, [openSplit, calculateSplitPosition, ref]);

    const hasActions = useMemo(() => {
      return (
        (item.itemTemplate.isUsable && onApply) ||
        (item.itemTemplate.isDroppable && onDrop) ||
        (item.itemTemplate.isEquippable && onEquip) ||
        onAddItemToConfiscation ||
        onUnEquip ||
        (!!item.attachedItems.length && onDetach) ||
        (item.itemTemplate.isRotatable && onMove) ||
        (item.itemTemplate.isTradable && onTrade) ||
        (item.itemTemplate.isStackable && onSplit && item.quantity > 1)
      );
    }, [
      item,
      onApply,
      onDrop,
      onEquip,
      onAddItemToConfiscation,
      onDetach,
      onMove,
      onTrade,
      onSplit,
    ]);

    if (!hasActions) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "min-w-[7.6rem] fixed bg-[#1A1A1A] p-3.5 rounded-[0.625rem] z-[9999] shadow-lg whitespace-nowrap flex flex-col",
          "[&>*:not(:first-child)]:pt-2.5 [&>*:not(:last-child)]:pb-2.5 [&>*]:border-b [&>*]:border-[#242424] [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 [&>*:last-child]:border-b-0",
          className,
        )}
        style={{
          top: position.y,
          left: position.x,
          boxShadow: "0px 0px 20px 0px #121212",
        }}
      >
        {item.itemTemplate.isUsable && onApply && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onApply(item.inventoryId, item);
              })
            }
          >
            Использовать
          </InventoryContextMenuButton>
        )}

        {item.itemTemplate.isDroppable && onDrop && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onDrop(item.inventoryId, item);
              })
            }
          >
            Выбросить
          </InventoryContextMenuButton>
        )}
        {onUnEquip && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onUnEquip?.(
                  equipmentId,
                  determineEquipmentSlotType(item.itemTemplate.type as ItemType),
                  item,
                );
              })
            }
          >
            Снять
          </InventoryContextMenuButton>
        )}

        {item.itemTemplate.isEquippable && onEquip && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onEquip(equipmentId || -1, {
                  slot: determineEquipmentSlotType(item.itemTemplate.type as ItemType),
                  item,
                });
              })
            }
          >
            Надеть
          </InventoryContextMenuButton>
        )}
        {onAddItemToConfiscation && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onAddItemToConfiscation(item.inventoryId, item);
              })
            }
          >
            Конфисковать
          </InventoryContextMenuButton>
        )}
        {!!item.attachedItems.length && onDetach && (
          <>
            {item.attachedItems.map((childItem) => (
              <InventoryContextMenuButton
                onClick={() =>
                  action(async () => {
                    await onDetach(item.inventoryId, childItem.inventoryId, item, childItem);
                  })
                }
                key={childItem.id}
              >
                {`Отсоеденить ${childItem.itemTemplate.name}`}
              </InventoryContextMenuButton>
            ))}
          </>
        )}
        {item.itemTemplate.isRotatable && onMove && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onMove(
                  item.inventoryId,
                  item.inventoryId,
                  {
                    ...item,
                    position: {
                      x: item.position.x,
                      y: item.position.y,
                    },
                    isRotated: !item.isRotated,
                  },
                  item,
                );
              })
            }
          >
            Повернуть
          </InventoryContextMenuButton>
        )}
        {item.itemTemplate.isTradable && onTrade && (
          <InventoryContextMenuButton
            onClick={() =>
              action(async () => {
                await onTrade();
              })
            }
          >
            Обменять
          </InventoryContextMenuButton>
        )}
        {item.itemTemplate.isStackable && onSplit && item.quantity > 1 && (
          <>
            {openSplit && (
              <div
                className={clsx(
                  "text-white absolute bg-[#1A1A1A] p-3.5 rounded-[0.625rem]",
                  "flex flex-col w-[15rem] top-0",
                  {
                    "ml-3 left-full": splitPosition === "right",
                    "mr-3 right-full": splitPosition === "left",
                  },
                )}
                style={{ boxShadow: "0px 0px 20px 0px #121212" }}
              >
                <div className="text-15 font-medium">Разделить</div>
                <div className="text-14 font-medium text-white/50">{item.itemTemplate.name}</div>

                <div className="flex items-center gap-1 mt-3.5">
                  <div
                    className={clsx(
                      "flex items-center justify-center shrink-0",
                      "border border-[#242424] rounded-[0.5rem] text-14 text-white/50 w-[3rem] h-[2.25rem]",
                    )}
                  >
                    1
                  </div>
                  <TextInput
                    value={splitQuantity}
                    onChange={(value) => {
                      if (Number(value) <= item.quantity && Number(value) >= 1) {
                        setSplitQuintity(String(value));
                      }
                    }}
                    inputClassName="h-[2.25rem] bg-dark-900/50 border border-[#242424]"
                    className="grow text-center "
                    type="number"
                  />
                  <div
                    className={clsx(
                      "flex items-center justify-center shrink-0",
                      "border border-[#242424] rounded-[0.5rem] text-14 text-white/50 w-[3rem] h-[2.25rem]",
                    )}
                  >
                    {item.quantity}
                  </div>
                </div>
                <div className="px-3 my-5">
                  <Slider
                    min={1}
                    max={item.quantity}
                    value={[Number(splitQuantity)]}
                    onValueChange={(value) => {
                      setSplitQuintity(String(value[0]));
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <SoundButton
                    color="gray"
                    variant="soft"
                    className="h-[2.25rem] capitalize"
                    onClick={toggleSplit}
                  >
                    Отмена
                  </SoundButton>
                  <SoundButton
                    variant="solid-surface-gradient"
                    className="h-[2.25rem] capitalize"
                    onClick={async (e) => {
                      onClose();
                      toggleSplit();
                      e.stopPropagation();
                      await onSplit(item.inventoryId, item.id, Number(splitQuantity));
                      await onRefresh();
                    }}
                  >
                    Ок
                  </SoundButton>
                </div>
              </div>
            )}
            <InventoryContextMenuButton onClick={toggleSplit}>Разделить</InventoryContextMenuButton>
          </>
        )}
      </div>
    );
  },
);

InventoryContextMenu.displayName = "InventoryContextMenu";

export default InventoryContextMenu;
